import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"
import monthSelectPlugin from "flatpickr/dist/plugins/monthSelect"

export default class extends Controller {
  static targets = ["datepicker", "buttonText", "dropdown"]
  static values = {
    minDate: String,
    maxDate: String,
    startDate: String,
    endDate: String
  }

  connect() {
    this.isOpen = false
    this.selectedDates = []

    // Initialize flatpickr with month picker plugin
    this.fp = flatpickr(this.datepickerTarget, {
      mode: "range",
      dateFormat: "Y-m-d",
      minDate: this.minDateValue || undefined,
      maxDate: this.maxDateValue || undefined,
      defaultDate: this.getDefaultDates(),
      inline: true,
      plugins: [
        new monthSelectPlugin({
          shorthand: false,
          dateFormat: "Y-m-d",
          altFormat: "M Y"
        })
      ],
      onChange: (selectedDates) => {
        this.selectedDates = selectedDates
        this.updateButtonText()
      }
    })

    // Close dropdown when clicking outside
    this.boundCloseOnClickOutside = this.closeOnClickOutside.bind(this)
    document.addEventListener("click", this.boundCloseOnClickOutside)
  }

  disconnect() {
    if (this.fp) {
      this.fp.destroy()
    }
    document.removeEventListener("click", this.boundCloseOnClickOutside)
  }

  getDefaultDates() {
    const dates = []
    if (this.startDateValue) {
      dates.push(this.startDateValue)
    }
    if (this.endDateValue) {
      dates.push(this.endDateValue)
    }
    return dates.length > 0 ? dates : undefined
  }

  toggle(event) {
    event.stopPropagation()
    this.isOpen = !this.isOpen
    this.dropdownTarget.classList.toggle("hidden", !this.isOpen)
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target) && this.isOpen) {
      this.isOpen = false
      this.dropdownTarget.classList.add("hidden")
    }
  }

  updateButtonText() {
    if (this.selectedDates.length === 2) {
      const start = this.selectedDates[0]
      const end = this.selectedDates[1]

      if (this.isSameMonth(start, end)) {
        this.buttonTextTarget.textContent = this.formatMonth(start)
      } else {
        this.buttonTextTarget.textContent = `${this.formatMonthShort(start)} - ${this.formatMonthShort(end)}`
      }
    } else if (this.selectedDates.length === 1) {
      this.buttonTextTarget.textContent = this.formatMonth(this.selectedDates[0])
    } else {
      this.buttonTextTarget.textContent = "All Time"
    }
  }

  isSameMonth(date1, date2) {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth()
  }

  formatMonth(date) {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  }

  formatMonthShort(date) {
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  }

  formatDateForUrl(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  apply() {
    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)

    params.delete('start_date')
    params.delete('end_date')

    if (this.selectedDates.length >= 1) {
      params.set('start_date', this.formatDateForUrl(this.selectedDates[0]))
    }
    if (this.selectedDates.length === 2) {
      params.set('end_date', this.formatDateForUrl(this.selectedDates[1]))
    } else if (this.selectedDates.length === 1) {
      params.set('end_date', this.formatDateForUrl(this.selectedDates[0]))
    }

    window.location.href = `${url.pathname}?${params.toString()}`
  }

  reset() {
    this.fp.clear()
    this.selectedDates = []
    this.updateButtonText()

    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)
    params.delete('start_date')
    params.delete('end_date')
    window.location.href = `${url.pathname}?${params.toString()}`
  }
}
